import { BaseEffect } from '../BaseEffect.js';

/**
 * Film grain effect implementation
 */
export class FilmGrainEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            grainAmount: 0.15,
            grainSize: 1.6,
            luminanceImpact: 0.5,
            temporalChange: 0.4,
            animate: true
        };

        super('film-grain', shaderManager, defaultParams);
    }

    /**
     * Get UI configuration for parameters
     * @returns {Object} UI configuration
     */
    getUIConfig() {
        return {
            grainAmount: {
                label: 'Grain Amount',
                min: 0,
                max: 1,
                step: 0.01
            },
            grainSize: {
                label: 'Grain Size',
                min: 0.1,
                max: 5,
                step: 0.1
            },
            luminanceImpact: {
                label: 'Luminance Impact',
                min: 0,
                max: 1,
                step: 0.01
            },
            temporalChange: {
                label: 'Temporal Change',
                min: 0,
                max: 1,
                step: 0.01
            },
            animate: {
                label: 'Animate'
            }
        };
    }

    /**
     * Initialize the effect
     */
    async init() {
        const vertexShaderSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentShaderSource = `
            precision mediump float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform float u_grainAmount;
            uniform float u_grainSize;
            uniform float u_luminanceImpact;
            uniform float u_temporalChange;
            
            varying vec2 v_texcoord;
            
            // Pseudo-random number generator
            float random(vec2 co) {
                return fract(sin(dot(co.xy ,vec2(12.9898,78.233))) * 43758.5453);
            }
            
            // Noise function
            float noise(vec2 p) {
                vec2 ip = floor(p);
                vec2 u = fract(p);
                u = u * u * (3.0 - 2.0 * u);
                
                float res = mix(
                    mix(random(ip), random(ip + vec2(1.0, 0.0)), u.x),
                    mix(random(ip + vec2(0.0, 1.0)), random(ip + vec2(1.0, 1.0)), u.x),
                    u.y
                );
                return res * res;
            }
            
            void main() {
                vec4 color = texture2D(u_image, v_texcoord);
                
                // Calculate luminance
                float luminance = dot(color.rgb, vec3(0.299, 0.587, 0.114));
                
                // Generate noise based on position and time
                vec2 noiseCoord = v_texcoord * u_resolution / u_grainSize;
                float n = noise(noiseCoord + u_time * u_temporalChange * 10.0);
                
                // Adjust noise based on luminance
                float noiseAmount = mix(u_grainAmount, u_grainAmount * 0.5, luminance * u_luminanceImpact);
                
                // Apply noise to color
                vec3 noiseColor = color.rgb + (n - 0.5) * noiseAmount;
                
                gl_FragColor = vec4(noiseColor, color.a);
            }
        `;

        await this.shaderManager.loadProgram(this.name, vertexShaderSource, fragmentShaderSource);
    }

    /**
     * Update effect-specific uniforms
     * @param {Object} uniforms - Uniform object to update
     */
    updateUniforms(uniforms) {
        uniforms['u_grainAmount'] = { value: this.params.grainAmount, type: '1f' };
        uniforms['u_grainSize'] = { value: this.params.grainSize, type: '1f' };
        uniforms['u_luminanceImpact'] = { value: this.params.luminanceImpact, type: '1f' };
        uniforms['u_temporalChange'] = { value: this.params.animate ? this.params.temporalChange : 0.0, type: '1f' };
    }
} 